import { useState } from 'react';

import {
  Button,
  Column,
  Row,
  TextInput,
} from '../../../components';
import {
  useAuth,
  useTownAssets,
} from '../../../stores';
import { config } from '../../../utils';
import webauthnKeyGesture from '../../../utils/secondFactor/webauthnKeyGesture';
import webauthnSupported from '../../../utils/secondFactor/webauthnSupported';

const codePattern = /^([0-9]{6}|[a-hjkmnp-z2-9]{5}-[a-hjkmnp-z2-9]{5})$/;

function LoginStepCheck() {
  const { auth } = useAuth();
  const { assets } = useTownAssets();
  const { translate } = assets;
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [badCode, setBadCode] = useState(false);
  const [keyRefusal, setKeyRefusal] = useState();

  const held = auth.server.loginPending?.methods?.held ?? ['totp'];
  const keyOffered = held.includes('webauthn') && webauthnSupported();

  const trimmedCode = code.trim().toLowerCase();
  const sendable = codePattern.test(trimmedCode) && (!pending);

  const check = async () => {
    if (!sendable) return;

    setPending(true);
    setBadCode(false);

    const answer = await auth.server.loginStep(
      config.town.back.auth.loginStep.check,
      { code: trimmedCode },
    );

    // Once the session is open this form is gone, and must not touch its state
    if (answer.done) return;

    setPending(false);
    if (answer.badCode) {
      setBadCode(true);
      setCode('');
    }
  };

  const handleKeyDown = (event) => {
    // Use void to make it clear we do not want to wait this async function:
    if (event.key === 'Enter') void check();
  };

  const useKey = async () => {
    setPending(true);
    setBadCode(false);
    setKeyRefusal();

    const options = await auth.server.loginStep(config.town.back.auth.loginStep.webauthnOptions);
    if (!options.options) {
      setPending(false);
      if (!options.loginPendingExpired) setKeyRefusal('secondFactorKeyRefused');
      return;
    }

    const gesture = await webauthnKeyGesture('authenticate', options.options);
    if (gesture.cancelled) {
      setPending(false);
      setKeyRefusal('secondFactorKeyCancelled');
      return;
    }

    const answer = await auth.server.loginStep(
      config.town.back.auth.loginStep.webauthnCheck,
      { response: gesture.response },
    );

    // Once the session is open this form is gone, and must not touch its state
    if (answer.done) return;

    setPending(false);
    if (!answer.loginPendingExpired) setKeyRefusal('secondFactorKeyRefused');
  };

  return (
    <Column gap={4} className="max-w-100">
      <span className="font-semibold">{translate('twoFactorLogin')}</span>

      {(keyOffered)
        ? (
            <Button
              label={translate('secondFactorUseKey', 'statements')}
              onClick={useKey}
              disabled={pending}
              pending={pending}
              baseColor
            />
          )
        : null}

      {(keyRefusal)
        ? <span className="text-pxUiAlert">{translate(keyRefusal, 'statements')}</span>
        : null}

      <span>
        {translate(
          (held.includes('totp')) ? 'secondFactorCheckHint' : 'secondFactorCheckHintKey',
          'statements',
        )}
      </span>

      <TextInput
        id="secondFactorCode"
        name="secondFactorCode"
        fillType="oneTimeCode"
        value={code}
        onChange={(event) => {
          setCode(event.target.value);
          setBadCode(false);
        }}
        onKeyDown={handleKeyDown}
        placeholder={translate('code')}
        widthChars={16}
        disabled={pending}
        focus={!keyOffered}
      />

      {(badCode)
        ? <span className="text-pxUiAlert">{translate('secondFactorBadCode', 'statements')}</span>
        : <span>{' '}</span>}

      <Row gap={6}>
        <Button
          label={translate('cancel')}
          onClick={auth.server.loginStepCancel}
          neutralColor
        />

        <Button
          label={translate('confirm')}
          onClick={check}
          disabled={!sendable}
          pending={pending}
          baseColor
        />
      </Row>
    </Column>
  );
}

export default LoginStepCheck;
